<template>
  <BaseModal
    :modalTitle="$t('W: Modtag ordrer')"
    contentPadding="none">
    <div class="flex flex-col gap-4 px-6 pb-6">
      <div class="relative">
        <IconLucideSearch
          class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-text-secondary" />
        <input
          v-model="searchText"
          type="text"
          class="h-9 w-full rounded-full border border-border-subtle bg-surface pr-3 pl-9 text-sm focus:outline-none"
          :placeholder="$t('W: Søg på ordrenummer, sagsnummer, kunde eller prøve-id...')" />
      </div>

      <div
        v-if="orderCards.length === 0"
        class="py-8 text-center text-sm text-text-secondary">
        {{ $t('W: Ingen ordrer venter på at blive modtaget') }}
      </div>

      <div
        v-else
        class="flex max-h-100 flex-col gap-2 overflow-y-auto">
        <button
          v-for="card in orderCards"
          :key="card.order.id"
          type="button"
          class="flex cursor-pointer items-start justify-between gap-3 rounded-lg border border-border-subtle p-3 text-left hover:bg-surface-muted"
          @click="onOrderClick(card.order.id)">
          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="truncate text-sm font-bold text-text-primary">
              {{ card.identifier.title }}
            </span>
            <span
              v-if="card.identifier.subtitle"
              class="text-xs text-text-secondary">
              {{ card.identifier.subtitle }}
            </span>
            <span class="text-xs text-text-secondary">{{ card.customerName }}</span>
          </div>
          <div class="flex shrink-0 items-center gap-1.5">
            <UiStatusChip
              v-if="card.timeChip"
              :variant="card.timeChip.variant"
              :label="card.timeChip.label" />
            <UiStatusChip
              :variant="card.statusChip.variant"
              :label="card.statusChip.label" />
          </div>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import {
  findOrderOpeningHours,
  getOrderedDeliveries,
  isDeliveryDelivered,
} from '@team-milva/lab-utils/orderDeliveries'
import { getSampleLabel } from '@/globals/typescript/utils/sampleLabel'
import { getTimeRemainingChip } from '@/globals/typescript/utils/timeRemainingChip'
import type { Order } from '@/globals/typescript/models/Order'
import { useAccountStore } from '@/stores/accountStore'
import { useClosedDayStore } from '@/stores/closedDayStore'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useAppStore } from '@/stores/appStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { resolveOrderOwnerName } from '@/globals/typescript/utils/orderOwner'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import type { TStatusChipVariant } from '@team-milva/lab-components'
import { mapActions, mapState } from 'pinia'

export default {
  name: 'ReceiveOrdersModal',

  emits: ['close'],

  data() {
    return {
      searchText: '',
    }
  },

  computed: {
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useCustomerAccountStore, ['allCustomerAccounts']),
    ...mapState(useInsuranceCustomerAccountStore, ['allInsuranceCustomerAccounts']),
    ...mapState(useCustomerUserStore, ['customerUsers']),
    ...mapState(useTurnaroundTimeStore, ['allTATs']),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useDropPointStore, ['labDropPoints']),
    ...mapState(useClosedDayStore, ['closedDates']),
    ...mapState(useAppStore, ['currentLocale']),

    customerAccountsById(): Record<string, { name: string }> {
      return Object.fromEntries(
        this.allCustomerAccounts.map((customerAccount) => [
          customerAccount.id,
          customerAccount,
        ])
      )
    },

    insuranceCustomerAccountsById(): Record<string, { name: string }> {
      return Object.fromEntries(
        this.allInsuranceCustomerAccounts.map((insuranceCustomerAccount) => [
          insuranceCustomerAccount.id,
          insuranceCustomerAccount,
        ])
      )
    },

    customerUsersById(): Record<string, { fullName: string }> {
      return Object.fromEntries(
        this.customerUsers.map((user) => [user.id, user])
      )
    },

    ordersNeedingReceipt() {
      return this.allOrders.filter(
        (order) =>
          order.statuses.orderStatus === 'confirmed' &&
          (order.statuses.sampleRegistrationStatus === 'in-transit' ||
            order.statuses.sampleRegistrationStatus === 'handed-over-partially')
      )
    },

    // Searches order number, case number, customer name, and any sample's label/code on the order.
    filteredOrders() {
      const search = this.searchText.trim().toLowerCase()
      if (!search) return this.ordersNeedingReceipt

      return this.ordersNeedingReceipt.filter((order) => {
        if (order.orderNumber.toLowerCase().includes(search)) return true
        if (order.caseNumber.toLowerCase().includes(search)) return true
        if (this.getCustomerName(order).toLowerCase().includes(search)) return true
        return order.samples.some(
          (sample) =>
            sample.label.id.toLowerCase().includes(search) ||
            getSampleLabel(sample.sampleNumber, this.currentLocale)
              .toLowerCase()
              .includes(search)
        )
      })
    },

    orderCards() {
      return this.filteredOrders.map((order) => ({
        order,
        identifier: this.getIdentifier(order),
        customerName: this.getCustomerName(order),
        statusChip: order.getStatusChip(),
        timeChip: this.getTimeChip(order),
      }))
    },
  },

  methods: {
    ...mapActions(useModalStore, ['open']),

    // Case number is the bold title when present; the order number then moves
    // to a secondary line. With no case number, the order number is the title.
    getIdentifier(order: Order): { title: string; subtitle: string | null } {
      if (order.caseNumber) {
        return { title: order.caseNumber, subtitle: `#${order.orderNumber}` }
      }
      return { title: `#${order.orderNumber}`, subtitle: null }
    },

    getCustomerName(order: Order): string {
      const user =
        this.customerUsersById[order.customerUserID || order.privateUserID || '']

      if (order.segment === 'private') {
        return user?.fullName ?? '—'
      }
      return (
        resolveOrderOwnerName(order, {
          customerAccountsById: this.customerAccountsById,
          insuranceCustomerAccountsById: this.insuranceCustomerAccountsById,
        }) ?? '—'
      )
    },

    // Same computation as the main Orders list — earliest not-yet-sent delivery.
    getTimeChip(
      order: Order
    ): { variant: TStatusChipVariant; label: string } | null {
      if (order.statuses.orderStatus === 'cancelled') return null

      const deliveries = getOrderedDeliveries(
        order.tatGroups,
        this.allTATs,
        this.currentLocale,
        order.samples,
        {
          dropoff: order.sampleHandOver?.dropoff ?? null,
          isSelfSend: order.sampleHandOver?.method === 'self-send',
          labWorkingDays: this.labAccount?.workingDays,
          closedDates: this.closedDates,
          tatStartDate: order.timestamps.tatStartDate ?? null,
          frozenSameDayDeadline: order.sameDayDeadline ?? null,
          openingHours: findOrderOpeningHours(
            this.labDropPoints,
            order.dropPoint?.id
          ),
        }
      )
      const nextDelivery = deliveries
        .filter((x) => !isDeliveryDelivered(x.labReportStatus))
        .sort((a, b) => a.expectedDeliveryMs - b.expectedDeliveryMs)[0]

      if (!nextDelivery) return null
      return getTimeRemainingChip(
        nextDelivery.expectedDeliveryMs,
        this.labAccount?.orderPriorityConfig
      )
    },

    onOrderClick(orderID: string): void {
      this.open({
        name: 'ReceiveOrderSamplesModal',
        width: 'lg',
        props: { orderID },
      })
    },
  },
}
</script>
